import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, MapPin, Car, Bus, Phone, Clock, Shield, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { APARTMENT_DATA, COMMON_TRANSLATIONS } from '@/data/apartmentData';

const detectLang = () => {
  const stored = sessionStorage.getItem('guest_lang');
  if (stored) return stored;
  const bl = (navigator.language || '').slice(0, 2).toLowerCase();
  return ['it', 'en', 'de', 'fr'].includes(bl) ? bl : 'en';
};

const LANGS = [
  { code: 'it', label: 'IT' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
];

const PhotoGallery = ({ photos }) => {
  const [idx, setIdx] = useState(0);
  if (!photos || photos.length === 0) return null;
  return (
    <div className="relative">
      <div className="overflow-hidden rounded-lg">
        <img src={photos[idx]} alt={`Photo ${idx + 1}`} className="w-full h-auto max-h-[500px] object-contain bg-gray-100 rounded-lg" />
      </div>
      {photos.length > 1 && (
        <div className="flex justify-center items-center gap-4 mt-3">
          <button onClick={() => setIdx(i => (i - 1 + photos.length) % photos.length)}
            className="w-10 h-10 rounded-full bg-[#65c3ba] text-white flex items-center justify-center hover:bg-[#3e716c]">
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm text-[#0A1312]">{idx + 1} / {photos.length}</span>
          <button onClick={() => setIdx(i => (i + 1) % photos.length)}
            className="w-10 h-10 rounded-full bg-[#65c3ba] text-white flex items-center justify-center hover:bg-[#3e716c]">
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

const ApartmentInstructions = () => {
  const { apartment } = useParams();
  const [params] = useSearchParams();
  const [lang, setLang] = useState(detectLang);
  const sessionId = params.get('session_id');
  const [verified, setVerified] = useState(false);

  const apt = APARTMENT_DATA[apartment];
  const t = COMMON_TRANSLATIONS[lang] || COMMON_TRANSLATIONS.en;

  // Verify payment
  useEffect(() => {
    if (!sessionId) { setVerified(true); return; } // Allow direct access for email links
    const API = process.env.REACT_APP_BACKEND_URL || '';
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts >= 10) { if (!cancelled) setVerified(true); return; }
      try {
        const res = await fetch(`${API}/api/guest/checkout-status/${sessionId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.payment_status === 'paid') { setVerified(true); return; }
        attempts++;
        setTimeout(poll, 2000);
      } catch { if (!cancelled) { attempts++; setTimeout(poll, 2000); } }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!apt) return <div className="min-h-screen flex items-center justify-center"><p>Apartment not found</p></div>;

  const inst = apt.instructions[lang] || apt.instructions.en;

  if (!verified) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <meta name="robots" content="noindex, nofollow" />
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#65c3ba] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-[#0A1312]">{t.paymentConfirmed}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="instructions-page">
      <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <div className="bg-[#65c3ba] text-white py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-4">
            {LANGS.map(l => (
              <button key={l.code} onClick={() => setLang(l.code)}
                className={`px-2 py-1 text-xs rounded ${lang === l.code ? 'bg-white text-[#3e716c] font-bold' : 'bg-white/20 text-white'}`}>
                {l.label}
              </button>
            ))}
          </div>
          <CheckCircle className="mx-auto mb-3" size={40} />
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{t.welcome}</h1>
          <p className="text-sm text-white/80">{apt.name}</p>
          <p className="text-white/90 mt-2">{t.welcomeSub}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">

        {/* Safebox Photo */}
        {apt.safeboxImage && (
          <div className="rounded-lg overflow-hidden shadow-md">
            <img src={apt.safeboxImage} alt="Safebox" className="w-full h-auto" />
          </div>
        )}

        {/* Safebox Code */}
        <div className="bg-[#F7F9F9] border-2 border-[#65c3ba] rounded-lg p-6 text-center" data-testid="safebox-section">
          <Shield className="mx-auto mb-2 text-[#65c3ba]" size={28} />
          <p className="text-[#0A1312] text-lg font-semibold mb-3">{t.safeboxTitle}</p>
          <div className="bg-[#3e716c] text-white rounded-lg py-5 px-6 inline-block" data-testid="safebox-code">
            <span className="text-5xl sm:text-7xl font-bold tracking-[0.3em]" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{apt.code}</span>
          </div>
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mt-4">
            <p className="text-[#0A1312] text-sm font-medium">{t.safeboxSave}</p>
          </div>
        </div>

        {/* Instructions */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-[#0A1312] mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{t.instructionsTitle}</h2>

          {/* Address + Warning */}
          <div className="bg-[#F7F9F9] border border-[#E2E8F0] rounded-lg p-5 mb-5">
            <div className="flex items-start gap-3">
              <MapPin className="text-[#65c3ba] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-sm text-[#0A1312]/60 mb-1">{t.addressLabel}</p>
                <p className="text-[#0A1312] font-semibold text-lg">{inst.address}</p>
                {inst.warning && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                    <p className="text-red-700 text-sm font-medium">{inst.warning}</p>
                  </div>
                )}
              </div>
            </div>
            <a href={apt.mapsLink} target="_blank" rel="noopener noreferrer" className="inline-block mt-4">
              <Button className="bg-[#65c3ba] text-white hover:bg-[#3e716c] rounded-lg">
                <MapPin size={16} className="mr-2" />{t.openMaps}
              </Button>
            </a>
          </div>

          {/* Check-in */}
          <div className="bg-[#F7F9F9] border border-[#E2E8F0] rounded-lg p-5 mb-5">
            <div className="flex items-start gap-3">
              <Clock className="text-[#65c3ba] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="font-semibold text-[#0A1312] mb-1">{t.checkinTitle}</p>
                <p className="text-[#0A1312]">{inst.checkin}</p>
                {inst.floor && (
                  <p className="text-[#0A1312] mt-2 font-medium">{inst.floor}</p>
                )}
              </div>
            </div>
          </div>

          {/* Parking */}
          <div className="bg-[#F7F9F9] border border-[#E2E8F0] rounded-lg p-5 mb-5">
            <div className="flex items-start gap-3">
              <Car className="text-[#65c3ba] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="font-semibold text-[#0A1312] mb-1">{t.parkingTitle}</p>
                <p className="text-[#0A1312] leading-relaxed">{inst.parking}</p>
              </div>
            </div>
            {apt.parkingMapsLink && (
              <a href={apt.parkingMapsLink} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 ml-8">
                <Button variant="outline" className="border-[#65c3ba] text-[#3e716c] hover:bg-[#65c3ba] hover:text-white rounded-lg">
                  <MapPin size={16} className="mr-2" />{t.parkingMaps}
                </Button>
              </a>
            )}
          </div>

          {/* Transport */}
          <div className="bg-[#F7F9F9] border border-[#E2E8F0] rounded-lg p-5 mb-5">
            <div className="flex items-start gap-3">
              <Bus className="text-[#65c3ba] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="font-semibold text-[#0A1312] mb-1">{t.transportTitle}</p>
                <p className="text-[#0A1312] leading-relaxed">{inst.transport}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Photo Gallery */}
        {apt.photos && apt.photos.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-[#0A1312] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{t.photosTitle}</h2>
            <PhotoGallery photos={apt.photos} />
          </div>
        )}

        {/* Contact Card */}
        <div className="bg-[#F7F9F9] border border-[#E2E8F0] rounded-lg p-6">
          <h2 className="text-xl font-bold text-[#0A1312] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{t.contactTitle}</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <MessageCircle className="text-[#65c3ba]" size={20} />
              <div>
                <p className="font-semibold text-[#0A1312]">Simone</p>
                <a href="https://wa.me/41786093451" className="text-[#65c3ba] hover:underline">+41 78 609 34 51</a>
              </div>
            </div>
            <p className="text-[#0A1312] text-sm">{t.contactPrefer}</p>
            <div className="border-t border-[#E2E8F0] pt-3 mt-3">
              <p className="text-sm text-[#0A1312]/70 mb-2">{t.contactUrgent}</p>
              <div className="flex items-center gap-3">
                <Phone className="text-[#0A1312]/40" size={18} />
                <div>
                  <p className="font-medium text-[#0A1312]">Alice</p>
                  <a href="https://wa.me/41797477718" className="text-[#0A1312]/70 hover:text-[#65c3ba]">+41 79 747 77 18</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Checkout Reminder */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-5 text-center">
          <Clock className="mx-auto mb-2 text-amber-600" size={24} />
          <p className="text-[#0A1312] font-semibold">{t.checkoutReminder}</p>
        </div>

      </div>
    </div>
  );
};

export default ApartmentInstructions;
